import { useTranslation } from 'react-i18next';
import type { Paragraphs } from 'types/capi/content';
import type { CapiProductPage } from 'types/capi/pdp';

import Content from '~/components/Content';
import Paragraph from '~/components/Content/Paragraph';
import PopoutContainer from '~/components/PopoutContainer';
import Tracked from '~/components/Tracked';
import useEnv from '~/hooks/useEnv';
import usePageData from '~/hooks/usePageData';

type Props = {
  isCollapsed?: boolean;
  onExpand?: () => void;
  showEditorialPolicy?: boolean;
};

const ProductDescription = ({
  isCollapsed = false,
  onExpand,
  showEditorialPolicy = true
}: Props) => {
  const { content } = usePageData<CapiProductPage>();
  const { SITE_BASE_URL } = useEnv();
  const { t } = useTranslation('pdp');

  const firstParagraph = content.find(
    (item): item is Paragraphs => item.type === 'paragraphs'
  );

  const previewText = firstParagraph?.items[0]?.content.items
    .map(({ text }) => text)
    .join('');

  return (
    <div
      data-cy="product-description"
      className="flex flex-col items-start gap-6"
    >
      <h2 className="text-title-sm">{t('description')}</h2>
      <div className="flex flex-col gap-6">
        {isCollapsed ? (
          <button
            type="button"
            onClick={onExpand}
            className="cursor-pointer text-left"
          >
            <p className="line-clamp-2 text-base leading-normal">
              {previewText}
            </p>
          </button>
        ) : (
          <Content content={content} />
        )}

        {showEditorialPolicy && !isCollapsed && (
          <PopoutContainer>
            <Paragraph>
              {t('commissionDisclaimer')}{' '}
              <Tracked.ExternalLink
                className="inline font-semibold underline"
                href={`${SITE_BASE_URL}/about#editorial-policy`}
                pagePosition="Body"
                variant="none"
                name="EDITORIAL_POLICY"
              >
                {t('editorialPolicyLink')}
              </Tracked.ExternalLink>
            </Paragraph>
          </PopoutContainer>
        )}
      </div>
    </div>
  );
};

export default ProductDescription;
